import { useEffect, useId, useRef, useState } from 'react';
import { AnchoredPanel } from './Anchored.js';
import { Button } from './Button.js';
import { Chip } from './Chip.js';
import { IconButton } from './IconButton.js';
import { BottomSheet } from './Overlay.js';
import type { FieldFrameProps } from './TextField.js';
import { iconSchedule } from './generated/icons.js';
import { SIDE_SHEET_QUERY, cx, useMediaQuery } from './util.js';

const pad = (n: number) => String(n).padStart(2, '0');

/** `14:30` shown as `2:30 pm`. */
export function formatTime(value: string): string {
  const m = /^(\d{2}):(\d{2})$/.exec(value);
  if (m === null) return '';
  const hour = Number(m[1]);
  return `${String(hour % 12 === 0 ? 12 : hour % 12)}:${m[2] ?? '00'} ${hour < 12 ? 'am' : 'pm'}`;
}

/** What a person types (`2:30 pm`, `230pm`, `14:30`, `9 am`) as `HH:MM`, or null. */
export function parseTime(text: string): string | null {
  const m = /^\s*(\d{1,2})(?::?(\d{2}))?\s*([ap])?\.?m?\.?\s*$/i.exec(text);
  if (m === null) return null;
  let hour = Number(m[1]);
  const minute = Number(m[2] ?? '0');
  const half = m[3]?.toLowerCase();
  if (minute > 59) return null;
  if (half === undefined) {
    if (hour > 23) return null;
  } else {
    if (hour < 1 || hour > 12) return null;
    hour = (hour % 12) + (half === 'p' ? 12 : 0);
  }
  return `${pad(hour)}:${pad(minute)}`;
}

export interface TimeFieldProps extends FieldFrameProps {
  /** `HH:MM` on a 24 hour clock, or an empty string for none. */
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  name?: string;
  placeholder?: string;
  invalidText?: string;
  openLabel?: string;
  closeLabel?: string;
  doneLabel?: string;
  hourLabel?: string;
  minuteLabel?: string;
  periodLabel?: string;
}

const HOURS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

/**
 * A time you can type (`2:30 pm`) or pick: hour, minute (every five) and am or pm. A bottom
 * sheet on a phone, a panel under the field on a wide window.
 */
export function TimeField({
  label,
  supporting,
  error,
  className,
  value,
  onChange,
  required = false,
  disabled = false,
  id,
  name,
  placeholder = 'hh:mm am',
  invalidText = 'Use a time like 2:30 pm.',
  openLabel = 'Choose a time',
  closeLabel = 'Close',
  doneLabel = 'Done',
  hourLabel = 'Hour',
  minuteLabel = 'Minute',
  periodLabel = 'am or pm',
}: TimeFieldProps) {
  const auto = useId();
  const inputId = id ?? `${auto}-input`;
  const noteId = `${auto}-note`;
  const wide = useMediaQuery(SIDE_SHEET_QUERY);
  const [text, setText] = useState(formatTime(value));
  const [bad, setBad] = useState(false);
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  // A new value from outside replaces what is shown, but never what is being typed.
  useEffect(() => {
    setText((shown) => (parseTime(shown) === value ? shown : formatTime(value)));
    setBad(false);
  }, [value]);

  const current = /^(\d{2}):(\d{2})$/.exec(value);
  const hour24 = current === null ? 9 : Number(current[1]);
  const minute = current === null ? 0 : Number(current[2]);
  const pm = hour24 >= 12;
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const set = (h12: number, m: number, isPm: boolean) =>
    onChange(`${pad((h12 % 12) + (isPm ? 12 : 0))}:${pad(m)}`);
  const shownError = error ?? (bad ? invalidText : undefined);
  const close = () => {
    setOpen(false);
    opener.current?.focus();
  };
  const picker = (
    <div className="k-timepick">
      <div role="group" aria-label={hourLabel} className="k-timepick__grid">
        {HOURS.map((h) => (
          <Chip
            key={h}
            selected={current !== null && hour12 === h}
            onClick={() => set(h, minute, pm)}
          >
            {String(h)}
          </Chip>
        ))}
      </div>
      <div role="group" aria-label={minuteLabel} className="k-timepick__grid">
        {MINUTES.map((m) => (
          <Chip
            key={m}
            selected={current !== null && minute === m}
            onClick={() => set(hour12, m, pm)}
          >
            {pad(m)}
          </Chip>
        ))}
      </div>
      <div role="group" aria-label={periodLabel} className="k-timepick__grid">
        <Chip selected={current !== null && !pm} onClick={() => set(hour12, minute, false)}>
          am
        </Chip>
        <Chip selected={current !== null && pm} onClick={() => set(hour12, minute, true)}>
          pm
        </Chip>
      </div>
      <Button size="lg" block onClick={close}>
        {doneLabel}
      </Button>
    </div>
  );
  return (
    <div className={cx('k-field', shownError !== undefined && 'k-field--error', className)}>
      <label htmlFor={inputId} className="k-field__label k-t-label-sm">
        {label}
      </label>
      <div ref={box} className="k-field__box k-field__box--trail">
        <input
          id={inputId}
          name={name}
          type="text"
          autoComplete="off"
          className="k-field__input k-t-body-lg"
          placeholder={placeholder}
          value={text}
          required={required}
          disabled={disabled}
          aria-invalid={shownError !== undefined || undefined}
          aria-describedby={
            shownError !== undefined || supporting !== undefined ? noteId : undefined
          }
          onChange={(e) => {
            setText(e.target.value);
            setBad(false);
            if (e.target.value === '' && !required) onChange('');
            else {
              const parsed = parseTime(e.target.value);
              if (parsed !== null) onChange(parsed);
            }
          }}
          onBlur={() => {
            if (text.trim() === '') return;
            const parsed = parseTime(text);
            if (parsed === null) setBad(true);
            else onChange(parsed);
          }}
        />
        <IconButton
          ref={opener}
          icon={iconSchedule}
          label={openLabel}
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={() => setOpen(!open)}
        />
      </div>
      {shownError !== undefined || supporting !== undefined ? (
        <p
          id={noteId}
          className="k-field__note k-t-label-sm"
          role={shownError === undefined ? undefined : 'alert'}
        >
          {shownError ?? supporting}
        </p>
      ) : null}
      {open && wide ? (
        <AnchoredPanel anchor={box} label={label} onClose={close} width={300}>
          {picker}
        </AnchoredPanel>
      ) : null}
      {!wide && open ? (
        <BottomSheet
          open={open}
          onClose={close}
          label={label}
          title={label}
          closeLabel={closeLabel}
        >
          {picker}
        </BottomSheet>
      ) : null}
    </div>
  );
}
